<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="mb-2 mb-md-0">
            <h1 class="h4 mb-1 font-weight-bold d-flex align-items-center flex-wrap">
              <span class="mr-2">
                {{ obj.id ? `Quotation ${obj.quotationNumber || ""}` : "New quotation" }}
              </span>
              <CBadge
                v-if="obj.id"
                :color="badgeColor('quotation', obj.statusDescription || 'Draft')"
                class="px-2 py-1"
              >
                {{ badgeLabel("quotation", obj.statusDescription || "Draft") }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{
                obj.id
                  ? "Edit the details, then save your changes"
                  : "Create a new quotation for your customer"
              }}
            </p>
          </div>
          <div class="d-flex align-items-center">
            <CDropdown
              v-if="obj.id"
              placement="bottom-end"
              toggler-text="More actions"
              color="light"
              class="tour-cdropdown"
            >
              <CDropdownItem @click="onConvertToInvoice(obj)">
                Convert to invoice
              </CDropdownItem>
              <CDropdownItem @click="onDuplicate(obj)">Duplicate</CDropdownItem>
              <template v-if="availableStatuses.length">
                <CDropdownDivider />
                <CDropdownHeader>Change status to</CDropdownHeader>
                <CDropdownItem
                  v-for="status in availableStatuses"
                  :key="status.id"
                  @click="requestStateChange(status)"
                >
                  {{ badgeLabel("quotation", status.name) }}
                </CDropdownItem>
              </template>
              <CDropdownDivider />
              <CDropdownItem @click="startTour">Take a tour</CDropdownItem>
            </CDropdown>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Main Form -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Quotation details</h2>
          </CCardHeader>
          <CCardBody class="p-4">
            <!-- Customer & Quotation Info -->
            <CRow>
              <CCol md="6">
                <div ref="customerField" tabindex="-1">
                  <CustomerSelectComponent
                    :selectedCustomer="selectedCustomer"
                    :customerItems="customerItemsWithAddNew"
                    @customer-selected="handleCustomerSelect"
                    @add-new-customer="addNewCustomer"
                  />
                  <div v-if="errors.customer" class="invalid-feedback d-block mb-3">
                    {{ errors.customer }}
                  </div>
                </div>

                <div
                  v-if="selectedCustomer && selectedCustomer.id !== 'add_new'"
                  class="customer-details p-3 bg-light rounded mb-3"
                >
                  <div class="d-flex align-items-center mb-2">
                    <div
                      class="c-avatar c-avatar-sm bg-primary text-white mr-2"
                    >
                      {{
                        selectedCustomer.name
                          ? selectedCustomer.name.charAt(0)
                          : "?"
                      }}
                    </div>
                    <div class="font-weight-bold">
                      {{ selectedCustomer.name }}
                    </div>
                  </div>
                  <div
                    class="small text-muted mb-1"
                    v-if="selectedCustomer.address"
                  >
                    <CIcon name="cil-location-pin" />
                    {{ selectedCustomer.address }}
                  </div>
                  <div
                    class="small text-muted mb-1"
                    v-if="selectedCustomer.contactEmail"
                  >
                    <CIcon name="cil-envelope-closed" />
                    {{ selectedCustomer.contactEmail }}
                  </div>
                  <div
                    class="small text-muted"
                    v-if="selectedCustomer.contactPhone"
                  >
                    <CIcon name="cil-phone" />
                    {{ selectedCustomer.contactPhone }}
                  </div>
                </div>

                <CFormGroup wrapperClasses="input-group pt-2">
                  <template #label>Contact person</template>
                  <template #input>
                    <CInput
                      placeholder="Contact person name"
                      v-model="obj.contactName"
                    />
                  </template>
                </CFormGroup>
              </CCol>

              <CCol md="6">
                <CFormGroup wrapperClasses="input-group pt-2">
                  <template #label>Quotation number</template>
                  <template #input>
                    <CInput
                      placeholder="Auto-generated if left empty"
                      v-model="obj.quotationNumber"
                    />
                  </template>
                </CFormGroup>

                <CRow form class="form-group">
                  <CCol sm="6">
                    <label class="col-form-label" for="quotation-issued-date">Issue date *</label>
                    <CInput
                      id="quotation-issued-date"
                      ref="issuedDateField"
                      type="date"
                      :value="computeIssuedDate"
                      :is-valid="errors.issuedDate ? false : null"
                      :invalid-feedback="errors.issuedDate"
                      @change="setIssuedDate"
                    />
                  </CCol>
                  <CCol sm="6">
                    <label class="col-form-label" for="quotation-expiry-date">Expiry date</label>
                    <CInput
                      id="quotation-expiry-date"
                      ref="expiryDateField"
                      type="date"
                      :value="computeExpiryDate"
                      :is-valid="errors.expiryDate ? false : null"
                      :invalid-feedback="errors.expiryDate"
                      @change="setExpiryDate"
                    />
                  </CCol>
                </CRow>

                <CRow form class="form-group">
                  <CCol sm="6" class="d-flex align-items-center">
                    <CSwitch
                      class="mr-2"
                      color="primary"
                      :checked.sync="showIssueDateInPrint"
                    />
                    <label class="mb-0 small text-muted">
                      <CIcon name="cil-print" size="sm" class="mr-1" />
                      Show issue date in print
                    </label>
                  </CCol>
                  <CCol sm="6" class="d-flex align-items-center">
                    <CSwitch
                      class="mr-2"
                      color="primary"
                      :checked.sync="showDueDateInPrint"
                    />
                    <label class="mb-0 small text-muted">
                      <CIcon name="cil-print" size="sm" class="mr-1" />
                      Show expiry date in print
                    </label>
                  </CCol>
                </CRow>

                <CFormGroup wrapperClasses="input-group pt-2">
                  <template #label>Reference</template>
                  <template #input>
                    <CInput
                      placeholder="PO number or reference"
                      v-model="obj.reference"
                    />
                  </template>
                </CFormGroup>
              </CCol>
            </CRow>

            <CFormGroup>
              <template #label>Quotation title</template>
              <template #input>
                <CTextarea
                  v-model="obj.title"
                  placeholder="Enter a descriptive title for this quotation"
                  rows="2"
                />
              </template>
            </CFormGroup>

            <!-- Quotation Items -->
            <InvoiceItemsComponent
              :items="quotationItems"
              :productItems="computeProductItemsWithAddNew"
              :grandTotal="grandTotal"
              :grandTaxOnly="grandTaxOnly"
              :grandTotalWithTax="grandTotalWithTax"
              :selectedSalesTax="selectedSalesTax"
              :salesTaxItems="salesTaxItemsWithAddNew"
              itemType="quotation"
              :showTax="true"
              :useDescriptionModal="true"
              @add-item="addNewItem"
              @remove-item="onRemoveClaimItem"
              @move-item="moveItem"
              @product-selected="handleProductSelect"
              @tax-selected="handleSalesTaxSelect"
              @add-new-tax="addNewSalesTax"
            />
            <div
              v-if="errors.items"
              ref="itemsField"
              tabindex="-1"
              class="invalid-feedback d-block"
            >
              {{ errors.items }}
            </div>

            <!-- Terms & Conditions -->
            <div class="mt-4">
              <CTextarea
                label="Terms and conditions"
                v-model="obj.note"
                placeholder="Enter your terms and conditions"
                rows="10"
              />
            </div>
          </CCardBody>
          <CCardFooter
            class="bg-transparent d-flex justify-content-between p-4"
          >
            <CButton color="light" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton
              color="primary"
              class="tour-submit-button"
              :disabled="saving"
              @click="submit"
            >
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              <CIcon v-else name="cil-check-circle" />
              {{ obj.id ? "Save" : "Create quotation" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Preview Section -->
    <CRow>
      <CCol lg="8">
        <CCard class="mb-4">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Preview</h2>
          </CCardHeader>
          <CCardBody class="p-0">
            <div class="quotation-preview border rounded">
              <WidgetsReportQuotation
                :quotation="computedPreviewItem"
                :showIssueDate="showIssueDateInPrint"
                :showDueDate="showDueDateInPrint"
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
      
      <!-- Sidebar -->
      <CCol lg="4">
        <QuotationSummaryComponent
          :grandTotal="grandTotal"
          :grandTaxOnly="grandTaxOnly"
          :grandTotalWithTax="grandTotalWithTax"
          :selectedSalesTax="selectedSalesTax"
          :canDownload="!!obj.id"
          :downloading="downloading"
          @print-quotation="printQuotation"
          @export-pdf="downloadPdf"
        />
      </CCol>
    </CRow>

    <!-- Status change confirm (cancel / reject) -->
    <CModal
      :show.sync="confirmStatusModal"
      :title="pendingStatus ? `${pendingStatusVerb} quotation ${obj.quotationNumber || ''}?` : ''"
      :close-on-backdrop="!changingState"
    >
      <p class="mb-0" v-if="pendingStatus">
        The quotation will be marked as
        <strong>{{ badgeLabel("quotation", pendingStatus.name).toLowerCase() }}</strong>.
        Your customer won't be able to accept it, but you can change the status again later.
      </p>
      <template #footer>
        <CButton color="light" :disabled="changingState" @click="confirmStatusModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="changingState" @click="confirmStateChange">
          <CSpinner v-if="changingState" size="sm" class="mr-1" />
          {{ pendingStatusVerb }} quotation
        </CButton>
      </template>
    </CModal>

    <!-- Modals -->
    <CustomerFormModal
      :show="addCustomerFormPopup"
      :customer="itemAddNewCustomer"
      @close="addCustomerFormPopup = false"
      @save="onCustomerPopupConfirmation"
    />

    <ProductFormModal
      :show="addPoductFormPopup"
      :product="itemAddNewProduct"
      @close="addPoductFormPopup = false"
      @save="onProductPopupConfirmation"
    />

    <SalesTaxFormModal
      :show="addSalesTaxFormPopup"
      :salesTax="itemAddNewSalesTax"
      @close="addSalesTaxFormPopup = false"
      @save="onSalesTaxPopupConfirmation"
    />
  </div>
</template>

<script>
import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";

import QuotationApi from "@/lib/quotationApi";
import CustomerApi from "@/lib/customerApi";
import SalesTaxApi from "@/lib/salesTaxApi";
import ProductApi from "@/lib/productApi";
import BusinessApi from "@/lib/businessApi";
import { mytToday, addDays, toApiDate, asDateString } from "@/utils/datetime";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";
import WidgetsReportQuotation from "../widgets/WidgetsReportQuotation";

// Import reusable components
import CustomerSelectComponent from "../components/CustomerSelectComponent";
import InvoiceItemsComponent from "../components/InvoiceItemsComponent";
import QuotationSummaryComponent from "../components/QuotationSummaryComponent";
import CustomerFormModal from "../components/CustomerFormModal";
import ProductFormModal from "../components/ProductFormModal";
import SalesTaxFormModal from "../components/SalesTaxFormModal";

export default {
  name: "Quotation",
  components: {
    WidgetsReportQuotation,
    CustomerSelectComponent,
    InvoiceItemsComponent,
    QuotationSummaryComponent,
    CustomerFormModal,
    ProductFormModal,
    SalesTaxFormModal,
  },
  data: () => {
    return {
      itemPendingNewProduct: null,
      businessApi: new BusinessApi(),
      itemAddNewProduct: {},
      addPoductFormPopup: false,
      itemAddNewCustomer: {},
      addCustomerFormPopup: false,
      itemAddNewSalesTax: {},
      addSalesTaxFormPopup: false,
      quotationStatuses: [],
      issuedDate: Date(),
      expiryDate: Date(),

      // Display settings for print/preview
      showIssueDateInPrint: true,
      showDueDateInPrint: true,

      quotationItems: [],
      selectedItem: null,
      selectedCustomer: null,
      selectedSalesTax: null,
      organizationTypeList: [],
      obj: {},
      submitted: false,
      saving: false,
      downloading: false,
      errors: {},
      confirmStatusModal: false,
      pendingStatus: null,
      changingState: false,
      api: new QuotationApi(),
      customerApi: new CustomerApi(),
      salesTaxApi: new SalesTaxApi(),
      productApi: new ProductApi(),
      customerItems: [],
      salesTaxItems: [],
      productItems: [],
    };
  },
  mounted() {
    var self = this;
    this.fetchQuotationStatuses();
    self.refreshCustomer();
    self.refreshProduct();
    self.refreshSalesTax();
    self.resetObj();
  },
  computed: {
    computedPreviewItem() {
      return {
        ...this.obj,
        items: this.computedQuotationItems,
        expiryDate: this.expiryDate,
      };
    },
    computeProductItemsWithAddNew() {
      return [
        ...this.productItems,
        { id: "add_new", name: "+ Add new product" },
      ];
    },
    customerItemsWithAddNew() {
      return [
        ...this.customerItems,
        { id: "add_new", name: "+ Add new customer" },
      ];
    },
    salesTaxItemsWithAddNew() {
      return [
        ...this.salesTaxItems,
        { id: "add_new", name: "+ Add new sales tax" },
      ];
    },
    computeExpiryDate() {
      return asDateString(this.expiryDate);
    },
    computeIssuedDate() {
      return asDateString(this.issuedDate);
    },
    // Only offer transitions away from the current status
    availableStatuses() {
      const current = this.obj.statusDescription || "Draft";
      return (this.quotationStatuses || []).filter((s) => s.name !== current);
    },
    pendingStatusVerb() {
      if (!this.pendingStatus) return "";
      return this.pendingStatus.name === "Rejected" ? "Reject" : "Cancel";
    },
    computedQuotationItems() {
      return this.quotationItems.map((item) => {
        return {
          ...item,
          productName: item.product?.name || "",
          totalAmountPerItem: this.getTotalItemPrice(item),
        };
      });
    },
    grandTotalWithTax() {
      return this.grandTotal + this.grandTaxOnly;
    },
    grandTaxOnly() {
      try {
        return (this.selectedSalesTax?.rateInPercentage * this.grandTotal) / 100 || 0;
      } catch (error) {
        return 0;
      }
    },
    grandTotal() {
      return this.quotationItems.reduce((total, item) => {
        return total + (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0);
      }, 0);
    },
  },
  methods: {
    badgeColor,
    badgeLabel,

    // Event handlers
    onDuplicate(item) {
      this.$router.push({
        path: `/tenants/Quotation/${item.id}/duplicate`,
      });
    },
    
    onConvertToInvoice(item) {
      this.$router.push({
        path: `/tenants/Invoice/${item.id}/convertFromQuot`,
      });
    },
    
    handleCustomerSelect(selected) {
      if (selected?.id === "add_new") {
        this.addNewCustomer();
        this.selectedCustomer = null;
      } else {
        this.selectedCustomer = selected;
      }
      if (this.submitted) this.validate();
    },
    
    handleProductSelect(selected, item) {
      if (selected?.id === "add_new") {
        this.itemPendingNewProduct = item;
        this.itemAddNewProduct = {};
        this.addPoductFormPopup = true;
      } else {
        item.product = selected;
      }
    },
    
    handleSalesTaxSelect(selected) {
      if (selected?.id === "add_new") {
        this.addNewSalesTax();
        this.selectedSalesTax = null;
      } else {
        this.selectedSalesTax = selected;
      }
    },
    
    // Modal handlers
    addNewCustomer() {
      this.itemAddNewCustomer = {};
      this.addCustomerFormPopup = true;
    },
    
    addNewSalesTax() {
      this.itemAddNewSalesTax = {};
      this.addSalesTaxFormPopup = true;
    },
    
    onCustomerPopupConfirmation(customer) {
      if (customer) {
        this.customerApi
          .create(customer)
          .then((response) => {
            const addedCustomer = response.result;
            this.refreshCustomer();
            this.selectedCustomer = addedCustomer;
            this.toast("Success", "Customer added successfully", "success");
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
      this.addCustomerFormPopup = false;
      this.itemAddNewCustomer = {};
    },
    
    onProductPopupConfirmation(product) {
      if (product) {
        this.productApi
          .create(product)
          .then((response) => {
            const addedProduct = response.result;
            this.refreshProduct();
            if (this.itemPendingNewProduct) {
              this.itemPendingNewProduct.product = addedProduct;
            }
            this.toast("Success", "Product added successfully", "success");
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
          });
      } else if (this.itemPendingNewProduct) {
        this.itemPendingNewProduct.product = null;
      }
      this.addPoductFormPopup = false;
      this.itemAddNewProduct = {};
    },
    
    onSalesTaxPopupConfirmation(salesTax) {
      if (salesTax) {
        this.salesTaxApi
          .create(salesTax)
          .then((response) => {
            const addedSalesTax = response.result;
            this.refreshSalesTax();
            this.selectedSalesTax = addedSalesTax;
            this.toast("Success", "Sales tax added successfully", "success");
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
      this.addSalesTaxFormPopup = false;
      this.itemAddNewSalesTax = {};
    },
    
    // Item management
    addNewItem() {
      const newPosition = this.quotationItems.length + 1;
      this.quotationItems.push({
        id: this.generateGUID(),
        product: this.productItems.length > 0 ? this.productItems[0] : { name: "" },
        price: 0,
        quantity: 1,
        unit: "L.S.",
        description: "",
        position: newPosition,
      });
    },
    
    onRemoveClaimItem(item) {
      const index = this.quotationItems.findIndex(i => i.id === item.id);
      if (index > -1) {
        this.quotationItems.splice(index, 1);
        this.updatePositions();
      }
    },
    
    moveItem(index, direction) {
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex >= 0 && newIndex < this.quotationItems.length) {
        const temp = this.quotationItems[index];
        this.$set(this.quotationItems, index, this.quotationItems[newIndex]);
        this.$set(this.quotationItems, newIndex, temp);
        this.updatePositions();
      }
    },
    
    updatePositions() {
      this.quotationItems.forEach((item, index) => {
        item.position = index + 1;
      });
    },
    
    getTotalItemPrice(item) {
      try {
        const quantity = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        return quantity * price;
      } catch (error) {
        return 0;
      }
    },
    
    // Utility methods
    generateGUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    
    setExpiryDate(e) {
      this.expiryDate = e;
      if (this.submitted) this.validate();
    },

    setIssuedDate(e) {
      this.issuedDate = e;
      if (this.submitted) this.validate();
    },

    // Inline validation. Returns true when the form can be saved.
    validate() {
      const errors = {};
      const customer = this.selectedCustomer;
      if (!customer || customer.id === "add_new") {
        errors.customer = "Choose the customer this quotation is for.";
      }
      const issued = asDateString(this.issuedDate);
      const expiry = asDateString(this.expiryDate);
      if (!issued) {
        errors.issuedDate = "Enter the issue date.";
      }
      if (issued && expiry && expiry < issued) {
        errors.expiryDate = "The expiry date must be on or after the issue date.";
      }
      if (!this.quotationItems.length) {
        errors.items = "Add at least one item to the quotation.";
      } else if (this.quotationItems.some((i) => !i.product || !i.product.id || i.product.id === "add_new")) {
        errors.items = "Choose a product for every item, or remove the empty rows.";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },

    focusFirstInvalid() {
      this.$nextTick(() => {
        const order = [
          ["customer", "customerField"],
          ["issuedDate", "issuedDateField"],
          ["expiryDate", "expiryDateField"],
          ["items", "itemsField"],
        ];
        const hit = order.find(([key]) => this.errors[key]);
        if (!hit) return;
        const ref = this.$refs[hit[1]];
        const el = ref && (ref.$el || ref);
        if (!el) return;
        if (el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
        const focusable = el.querySelector ? el.querySelector("input, [tabindex]") : null;
        (focusable || el).focus && (focusable || el).focus();
      });
    },
    
    // API methods
    fetchQuotationStatuses() {
      this.api
        .getQuotationStatus()
        .then((response) => {
          this.quotationStatuses = response.result;
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't load the quotation statuses. ${helper.getErrorMessage(data)}`);
        });
    },

    refreshCustomer() {
      this.customerApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.customerItems = response.result;
        })
        .catch(({ data }) => {});
    },
    
    refreshProduct() {
      this.productApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.productItems = response.result;
        })
        .catch(({ data }) => {});
    },
    
    refreshSalesTax() {
      this.salesTaxApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.salesTaxItems = response.result;
        })
        .catch((error) => {
          console.error('[Quotation] refreshSalesTax - Error loading sales tax:', error);
          this.salesTaxItems = [];
        });
    },
    
    // Main operations
    // Cancel / reject ask first (section 5.4); other transitions apply directly
    requestStateChange(status) {
      if (status.name === "Cancelled" || status.name === "Rejected") {
        this.pendingStatus = status;
        this.confirmStatusModal = true;
      } else {
        this.changeState(status);
      }
    },

    confirmStateChange() {
      if (this.pendingStatus) this.changeState(this.pendingStatus);
    },

    changeState(item) {
      if (!this.obj.id) return;
      this.obj.status = item.id;
      this.changingState = true;
      this.api
        .updateState(this.obj)
        .then(() => {
          this.resetObj();
          this.$toast.success(`Quotation marked as ${badgeLabel("quotation", item.name).toLowerCase()}`);
        })
        .catch(({ data }) => {
          this.$toast.error(`Couldn't change the status. ${helper.getErrorMessage(data)}`);
        })
        .finally(() => {
          this.changingState = false;
          this.confirmStatusModal = false;
          this.pendingStatus = null;
        });
    },
    
    resetObj() {
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.issuedDate = this.obj.issuedDate;
            this.expiryDate = this.obj.dueDate;
            this.selectedCustomer = this.obj.customer;
            this.selectedSalesTax = this.obj.salesTax;

            // Load display settings (default to true for backwards compatibility)
            this.showIssueDateInPrint = this.obj.showIssueDateInPrint !== undefined
              ? this.obj.showIssueDateInPrint
              : true;
            this.showDueDateInPrint = this.obj.showDueDateInPrint !== undefined
              ? this.obj.showDueDateInPrint
              : true;

            if (this.obj.items.length > 0) {
              if (this.obj.items[0].position == 0) {
                for (var i = 0; i < this.obj.items.length; i++) {
                  this.obj.items[i].position = i + 1;
                }
              }
            }
            this.quotationItems = this.obj.items;

            // Handle duplication logic
            if (this.$route.name === "QuotationDuplicate") {
              delete this.obj.id;
              this.issuedDate = mytToday();
              this.expiryDate = addDays(this.issuedDate, 30);

              this.api
                .getNextNumber()
                .then((response) => {
                  this.obj.quotationNumber = response.result;
                })
                .catch(({ data }) => {});

              this.obj.createdAt = null;
              this.obj.updatedAt = null;

              for (var i = 0; i < this.obj.items.length; i++) {
                delete this.obj.items[i].id;
              }

              this.toast("Info", "You are duplicating a quotation.", "info");
            }
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
          });
      } else {
        this.obj = this.getEmptyObj();

        this.issuedDate = mytToday();
        this.expiryDate = addDays(this.issuedDate, 30);

        this.api
          .getNextNumber()
          .then((response) => {
            this.obj.quotationNumber = response.result;
          })
          .catch(({ data }) => {});

        this.businessApi
          .getCurrentActiveBusiness()
          .then((response) => {
            this.obj.note = response.result.quotationDefaultFooter;
          })
          .catch(({ data }) => {});
      }
    },
    
    onSubmit() {
      if (this.saving) return;
      if (!this.validate()) {
        this.focusFirstInvalid();
        return;
      }
      this.saving = true;
      this.obj.items = this.computedQuotationItems;
      // Date-only fields go to the API as "yyyy-MM-dd" (never a Date: JSON turns it into UTC, 8 hours back)
      this.obj.issuedDate = toApiDate(asDateString(this.issuedDate)) || null;
      this.obj.dueDate = toApiDate(asDateString(this.expiryDate)) || null;

      // Save display settings
      this.obj.showIssueDateInPrint = this.showIssueDateInPrint;
      this.obj.showDueDateInPrint = this.showDueDateInPrint;

      this.obj.items.forEach((item) => {
        item.productId = item.product.id;
      });

      if (this.selectedCustomer) {
        this.obj.customerId = this.selectedCustomer.id;
      }
      if (this.selectedSalesTax) {
        this.obj.salesTaxId = this.selectedSalesTax.id;
      }

      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.$toast.success("Quotation created");
            const newObj = response.result;
            this.$router.push({
              path: `/tenants/Quotation/${newObj.id}`,
            });
          })
          .catch(({ data }) => {
            this.$toast.error(`Couldn't create the quotation. ${helper.getErrorMessage(data)}`);
          })
          .finally(() => {
            this.saving = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then(() => {
            this.$toast.success("Quotation saved");
            this.resetObj();
          })
          .catch(({ data }) => {
            this.$toast.error(`Couldn't save the quotation. ${helper.getErrorMessage(data)}`);
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    
    // UI Methods
    startTour() {
      const tour = new Shepherd.Tour({
        useModalOverlay: true,
        cancelIcon: { enabled: true },
        defaultStepOptions: {
          scrollTo: { behavior: "smooth", block: "center" },
          classes: "shadow-md",
        },
      });

      tour.addStep({
        id: "quotation-header",
        text: "This is the quotation header where you can see the quotation number and status.",
        attachTo: { element: ".tour-cdropdown", on: "bottom" },
        buttons: [{ text: "Next", action: tour.next }],
      });

      tour.addStep({
        id: "submit-button",
        text: "Click here to submit your completed quotation.",
        attachTo: { element: ".tour-submit-button", on: "right" },
        buttons: [
          { text: "Back", action: tour.back },
          { text: "Finish", action: tour.complete },
        ],
      });

      tour.start();
    },
    
    printQuotation() {
      window.print();
    },

    downloadPdf() {
      if (!this.obj.id || this.downloading) return;
      this.downloading = true;
      const name = this.obj.quotationNumber ? `${this.obj.quotationNumber}.pdf` : undefined;
      this.api
        .downloadPDF(this.obj.id, name)
        .catch(() => {
          this.$toast.error("Couldn't download the PDF. Check your connection and try again.");
        })
        .finally(() => {
          this.downloading = false;
        });
    },

    cancel() {
      this.$router.push({ path: "/tenants/QuotationList" });
    },

    // Kept for the remaining call sites; routes to the global toast
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning", info: "info" }[color] || "info";
      this.$toast[kind](message);
    },
    
    getEmptyObj() {
      return {
        quotationNumber: "",
      };
    },
    
    submit() {
      this.submitted = true;
      this.onSubmit();
    },
  },
};
</script>

<style scoped>
.customer-details {
  transition: all 0.3s ease;
}

.quotation-preview {
  transform: scale(0.8);
  transform-origin: top center;
  overflow: hidden;
}

@media (max-width: 767.98px) {
  .quotation-preview {
    transform: scale(0.7);
  }
}
</style>